import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useLayoutEffect,
} from 'react';

import { Box, TextField, TextFieldProps, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { guards } from '@shared/helpers/typeguards.helpers';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { getDecimalDisplayNumber } from '@shared/helpers/display/get-decimal-display-number.helper';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import {
  QuantityUnit,
  quantityUnitToMathJSUnit,
} from '@shared/models/unit.model';
import { is } from '@client/helpers/dom.guards';
import { getInputTextWidth } from '@client/helpers/dom.helpers';
import { shouldFillEmptyFieldOnArrowRight } from '@client/helpers/empty-field-fill.helpers';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import { isQuantityUnit } from '@shared/helpers/unit.helpers';
import { useFocusInside } from '@client/hooks/use-events.hooks';
import { Tooltip } from '@ui/Tooltip';
import { useKineticNumber } from '@ui/KineticNumberDisplay';
import { usePrevious } from '@client/hooks/use-utils.hooks';
import { UnitButton } from './components/UnitButton';
import {
  parseCurrentNumberValue,
  unitNumberInputErrorTooltip,
} from './unit-number-input-error.helpers';
import { mathJS } from '@shared/helpers/mathjs';

export type UnitNumberInputProps = {
  /** Current numeric value (undefined = empty) */
  value?: number;
  /** Display-only placeholder when {@link UnitNumberInputProps.value} is empty. takes precedence over "placeholder" */
  fallback?: number;
  /** Change handler emitting parsed number or undefined */
  onChange: (value?: number) => void;
  /** Unit of the numeric value; '%' is treated as 0–100 display for 0–1 values */
  unit?: QuantityUnit | NonCollapsibleString;
  /** Display unit to render to the right (if different from `unit`). this disabled automatic value/unit calculation for placeholder */
  displayUnit?: string;
  /** Align unit widths vertically between siblings when inactive */
  unitAlignment?: 'tryVertical' | 'none';
  /** Min accepted numeric value (validated on input) */
  min?: number;
  /** Max accepted numeric value (validated on input) */
  max?: number;
  /** Visible label ({@link TextFieldProps.label}) */
  label?: TextFieldProps['label'];
  /** Helper text below the field ({@link TextFieldProps.helperText}) */
  helperText?: TextFieldProps['helperText'];
  /** Whether the field is in an error state */
  error?: Error | boolean;
  /**
   * Bound or validation message shown on hover when the typed value itself
   * is still inside min/max. A stored expression can sit outside those bounds
   * while the displayed number does not.
   */
  errorMessage?: string;
  /** Placeholder text ({@link TextFieldProps.placeholder}) */
  placeholder?: TextFieldProps['placeholder'];
  /** Converted number represented by {@link UnitNumberInputProps.placeholder}. */
  placeholderNumber?: number;
  /** Disables input and applies disabled styles ({@link TextFieldProps.disabled}) */
  disabled?: TextFieldProps['disabled'];
  /** Keeps styling active but prevents interaction; use for read-only UX */
  deactivated?: boolean;
  /** Text alignment inside the input */
  textAlignment?: 'left' | 'right';
  /**
   * When set, format the idle/focused display with this many fraction digits
   * (e.g. `1` → `20.0`). Omit to keep compact decimal display.
   */
  fractionDigits?: number;
  /** Controls outline visibility when idle (default: 'always') */
  outlineVisibility?: 'hover' | 'always';
  /** Optional start adornment element (e.g. icon) */
  startAdornment?: React.ReactNode;
  /** Visual variant ({@link TextFieldProps.variant}) */
  variant?: TextFieldProps['variant'];
  /** Size variant ({@link TextFieldProps.size}) */
  size?: TextFieldProps['size'];
  /** CSS class for the outer TextField */
  className?: TextFieldProps['className'];
  /** DOM id ({@link TextFieldProps.id}) */
  id?: TextFieldProps['id'];
  /** Input name ({@link TextFieldProps.name}) */
  name?: TextFieldProps['name'];
  /** Accessible name of the input, for fields rendered without a visible label */
  'aria-label'?: string;
  /** Ref to the underlying HTML input element */
  inputRef?: React.Ref<HTMLInputElement>;
  /** Focus handler ({@link TextFieldProps.onFocus}) */
  onFocus?: TextFieldProps['onFocus'];
  /** Unit click handler. If a promise is returned, it the input will resume focusing after the promise is resolved. */
  onUnitClick?: (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => Promise<void> | void;
  /** Blur handler ({@link TextFieldProps.onBlur}) */
  onBlur?: TextFieldProps['onBlur'];
  /** Key down handler ({@link TextFieldProps.onKeyDown}) */
  onKeyDown?: TextFieldProps['onKeyDown'];
  /**
   * When set, ArrowRight in an empty field fills this number and keeps focus
   * so the user can edit it. Tab still moves focus. ArrowRight with existing
   * text is left to the browser so the caret can step.
   */
  fillOnTabValue?: number;
  /** Optional callback to receive parse errors. When provided, the parent can display the error; this component will not render its own parse error text. */
  onParseErrorChange?: (error?: Error) => void;
  /** Forces active styling regardless of focus/hover state */
  active?: boolean;
};

const setRef = <Node extends Element | null>(
  ref: React.Ref<Node | null> | null | undefined,
  node: Node,
) => {
  if (guards.is.reactRefCallback(ref)) ref(node);
  if (guards.is.reactRefObject(ref)) ref.current = node;
};
const setRefs = <Node extends Element | null>(
  refs: (React.Ref<Node | null> | null | undefined)[],
  node: Node,
) => {
  for (const ref of refs) setRef(ref, node);
};

const combineRefs =
  <Node extends Element | null>(
    refs: (React.Ref<Node | null> | null | undefined)[],
  ) =>
  (node: Node) =>
    setRefs(refs, node);
export const UnitNumberInput = forwardRef<HTMLDivElement, UnitNumberInputProps>(
  (
    {
      id,
      name,
      value,
      fallback,
      unit,
      displayUnit,
      unitAlignment = 'tryVertical',
      min,
      max,
      deactivated = false,
      textAlignment = 'right',
      size = 'small',
      variant = 'outlined',
      error,
      errorMessage,
      placeholder,
      placeholderNumber,
      label,
      helperText,
      disabled,
      className,
      fractionDigits,
      outlineVisibility = 'always',
      active,
      startAdornment,
      inputRef,
      onChange,
      onUnitClick,
      onKeyDown,
      onFocus,
      onBlur,
      onParseErrorChange,
      fillOnTabValue,
      'aria-label': ariaLabel,
    },
    ref,
  ) => {
    const htmlInputRef = useRef<HTMLInputElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const unitClickSequenceRef = useRef(0);
    const [isUnitClickPending, setIsUnitClickPending] = useState(false);

    const correctInputPercentUnit = useCallback(
      (v: number): number => (unit === '%' ? v / 100 : v),
      [unit],
    );

    // get possible errors for number value (basically just min/max validation. Will check the raw value, not adjusted for percent or anything)
    const getValueError = useCallback(
      (value?: number) => {
        if (guards.is.undefined(value)) return undefined;
        const result = parseCurrentNumberValue(`${value}`, { min, max });
        return guards.is.error(result) ? result : undefined;
      },
      [min, max],
    );

    // raw current parse/validation error (tracked even while hidden)
    const [internalError, setInternalError] = useState<Error | undefined>(() =>
      getValueError(value),
    );

    // validation errors stay hidden until the field has been submitted once with
    // an error, then remain visible while corrupt and reset on a clean submit/blur.
    const [showValidationError, setShowValidationError] = useState(false);
    const effectiveError = showValidationError ? internalError : undefined;

    const [isFocused] = useFocusInside(rootRef);
    // The focus flag can lag the DOM (focusout with no next target). A controlled
    // value of "" would then wipe keystrokes while the input is still focused.
    const domFocused = htmlInputRef.current === document.activeElement;
    const editing = isFocused || domFocused;
    const [localNumberValue, setLocalNumberValue] = useState<
      Error | number | undefined
    >(value);

    const getLocalInputValue = useCallback(
      (
        numericValue?: number,
        unitForValue?: QuantityUnit | NonCollapsibleString,
      ): string => {
        if (guards.is.undefined(numericValue)) return '';

        const asPercent =
          unitForValue === '%'
            ? mathJS.bignumber(numericValue).mul(100).toNumber() // avoid floating point issues
            : numericValue;
        // Fixed fraction digits when requested (e.g. calibration length `20.0`).
        if (
          fractionDigits !== undefined &&
          Number.isInteger(fractionDigits) &&
          fractionDigits >= 0
        ) {
          return asPercent.toFixed(fractionDigits);
        }
        // decimal form (never scientific) so the focused value stays editable — DEV-3254
        return getDecimalDisplayNumber(asPercent);
      },
      [fractionDigits],
    );
    const currentInputValue = getLocalInputValue(value, unit);
    const prevValue = usePrevious(value);
    const prevMin = usePrevious(min);
    const prevMax = usePrevious(max);
    const [localInputValue, setLocalInputValue] = useState(currentInputValue);

    // sync external value changes when not focused
    if (!editing) {
      const valueChanged = value !== prevValue;
      const boundsChanged = min !== prevMin || max !== prevMax;
      if (valueChanged || boundsChanged) {
        // bounds can flip a previously valid value invalid, so re-run validation
        setInternalError(getValueError(value));
        // keep the principle: a fresh value resets visibility; bound tightening
        // alone should not surface an error until the user submits
        if (valueChanged) setShowValidationError(false);
      }
      if (currentInputValue !== localInputValue) {
        setLocalInputValue(currentInputValue);
      }
      const numberValuesAreNaN =
        Number.isNaN(value) && Number.isNaN(localNumberValue);
      if (value !== localNumberValue && !numberValuesAreNaN) {
        setLocalNumberValue(value);
      }
    }

    // emit only the visible error to parents so the principle propagates everywhere
    const lastEmittedErrorRef = useRef<string | undefined>(undefined);
    const lastCallbackRef = useRef<typeof onParseErrorChange>(undefined);
    useEffect(() => {
      if (!onParseErrorChange) return;
      const message = effectiveError?.message;
      // re-emit on callback swap so a new subscriber syncs even if message is equal
      const sameSubscriber = onParseErrorChange === lastCallbackRef.current;
      if (sameSubscriber && message === lastEmittedErrorRef.current) return;
      lastEmittedErrorRef.current = message;
      lastCallbackRef.current = onParseErrorChange;
      onParseErrorChange(effectiveError);
    }, [effectiveError, onParseErrorChange]);

    const focusInputAtEnd = useCallback(
      () => htmlInputRef.current?.focus({ preventScroll: true }),
      [],
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setTimeout(() => {
          // ugly, but since we're changing the value from text to number when focusing, we need to wait for the value to be updated
          if (htmlInputRef.current) {
            const valueLength = htmlInputRef.current.value.length;
            htmlInputRef.current.setSelectionRange(0, valueLength);
          }
          onFocus?.(e);
        }, 10);
      },
      [onFocus],
    );
    const resetLocalState = useCallback(() => {
      setLocalNumberValue(value);
      setLocalInputValue(currentInputValue);
      setInternalError(getValueError(value));
      setShowValidationError(false);
    }, [currentInputValue, value, getValueError]);

    type CommitAction = 'committed' | 'reset' | 'noop';
    const commit = useCallback(
      (options: { onErrorStrategy: 'doNothing' | 'reset' }): CommitAction => {
        if (guards.is.error(localNumberValue)) {
          switch (options.onErrorStrategy) {
            case 'doNothing':
              return 'noop';
            case 'reset':
              resetLocalState();
              return 'reset';
          }
        }
        const isValidForCommit =
          guards.is.finiteNumber(localNumberValue) ||
          guards.is.undefined(localNumberValue);

        // Only call onChange if the value actually changed
        if (value !== localNumberValue && isValidForCommit) {
          onChange(localNumberValue);
          return 'committed';
        }
        return 'noop';
      },
      [localNumberValue, onChange, resetLocalState, value],
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        // if the blur came from a child element, the input itself should still be focused
        if (rootRef.current?.contains(e.relatedTarget)) return;
        // blur resets corrupt input back to the last valid value, so errors clear
        commit({ onErrorStrategy: 'reset' });
        setShowValidationError(false);
        onBlur?.(e);
      },
      [commit, onBlur],
    );

    const handleKeyDownInternal = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
          case KeyboardKeys.ENTER: {
            const commitResult = commit({ onErrorStrategy: 'doNothing' });
            // first submit attempt reveals validation errors; a clean commit resets
            if (commitResult === 'noop' && guards.is.error(localNumberValue))
              setShowValidationError(true);
            else setShowValidationError(false);
            if (commitResult !== 'noop')
              setTimeout(() => htmlInputRef.current?.blur());

            break;
          }
          case KeyboardKeys.ESCAPE:
            resetLocalState();

            // ugly, but we need to wait for the value to be updated before blurring for reset to work
            setTimeout(() => htmlInputRef.current?.blur());
            break;
          case KeyboardKeys.ARROW_RIGHT: {
            // ArrowRight in an empty field fills the fallback so the user can
            // edit it further instead of retyping. Tab still moves focus.
            // Existing text is left alone so the caret can step.
            if (
              !shouldFillEmptyFieldOnArrowRight(event, localInputValue) ||
              guards.is.undefined(fillOnTabValue)
            )
              break;
            event.preventDefault();
            const filled = getLocalInputValue(fillOnTabValue, unit);
            setLocalInputValue(filled);
            setLocalNumberValue(fillOnTabValue);
            // place caret at the end once the filled value has rendered
            requestAnimationFrame(() => {
              const input = htmlInputRef.current;
              input?.setSelectionRange(input.value.length, input.value.length);
            });
            break;
          }
        }
        onKeyDown?.(event);
      },
      [
        commit,
        resetLocalState,
        onKeyDown,
        localNumberValue,
        localInputValue,
        fillOnTabValue,
        getLocalInputValue,
        unit,
      ],
    );
    const handleInput = useCallback(
      (event: React.InputEvent<HTMLDivElement>) => {
        const { target } = event;
        if (!is.Input(target)) return;
        const rawValue = target.value;
        const result = parseCurrentNumberValue(rawValue, {
          min,
          max,
          valueTransform: correctInputPercentUnit,
        });
        setLocalNumberValue(result);
        setLocalInputValue(rawValue);
        setInternalError(guards.is.error(result) ? result : undefined);
      },
      [correctInputPercentUnit, min, max],
    );

    const fallbackDisplayData = useMemo(() => {
      if (guards.is.undefined(fallback)) return undefined;
      if (guards.is.defined(displayUnit) || !isQuantityUnit(unit)) {
        return {
          value: getDisplayNumber(fallback),
          unit: displayUnit || unit,
        };
      }
      return getDisplayValueData({
        value: fallback,
        unit: quantityUnitToMathJSUnit(unit),
      });
    }, [displayUnit, fallback, unit]);

    const displayData = useMemo(() => {
      const activeUnit = displayUnit ?? unit ?? '';

      if (editing || isUnitClickPending)
        return { value: localInputValue, unit: activeUnit };

      // if we have no value, return empty right away
      if (guards.is.undefined(value)) {
        if (guards.is.undefined(fallbackDisplayData))
          return { value: '', unit: activeUnit };
        return {
          value: '',
          unit: fallbackDisplayData.unit ?? activeUnit,
        };
      }

      // Fixed fraction digits apply idle and focused (e.g. calibration `20.0`).
      if (fractionDigits !== undefined) {
        return {
          value: getLocalInputValue(value, unit),
          unit: activeUnit,
        };
      }

      // if we have a display unit, use it directly since it's a strict override
      if (guards.is.defined(displayUnit) || !isQuantityUnit(unit))
        return { value: getDisplayNumber(value), unit: displayUnit || unit };

      // otherwise, automatically calculate the value & unit with getDisplayValueData
      return getDisplayValueData({
        value,
        unit: quantityUnitToMathJSUnit(unit),
      });
    }, [
      value,
      unit,
      displayUnit,
      fallbackDisplayData,
      localInputValue,
      editing,
      isUnitClickPending,
      fractionDigits,
      getLocalInputValue,
    ]);

    const hasUnit =
      guards.is.nonEmptyString(displayData.unit) && displayData.unit !== 'none';
    const placeholderUnit = fallbackDisplayData?.unit ?? displayData.unit;
    const previousPlaceholderUnit = usePrevious(placeholderUnit, {
      setInitial: true,
    });
    const animatedPlaceholderNumber = useKineticNumber(placeholderNumber ?? 0, {
      animate:
        placeholderNumber !== undefined &&
        value === undefined &&
        !isFocused &&
        !isUnitClickPending &&
        previousPlaceholderUnit === placeholderUnit,
    });

    // to keep track of the rendered width of the contents
    const [valueDisplayWidth, setValueDisplayWidth] = useState<
      undefined | `${number}px`
    >(undefined);

    const hasForcedActive = active === true;
    const visualFocus = hasForcedActive ? true : editing || isUnitClickPending;
    const { classes, cx } = useStyles({
      inputTextAlignment: textAlignment,
      hasUnit,
      unitAlignment,
      isFocused: visualFocus,
      deactivated,
      valueDisplayWidth,
      outlineVisibility,
    });

    const placeholderText = guards.is.defined(fallback)
      ? fallbackDisplayData?.value
      : placeholderNumber === undefined
        ? placeholder
        : getDisplayNumber(animatedPlaceholderNumber);
    const activeDisplayValueText = displayData.value || placeholderText;

    useLayoutEffect(() => {
      const input = htmlInputRef.current;
      if (!input || editing || isUnitClickPending) {
        setValueDisplayWidth(undefined);
        return;
      }
      const width = getInputTextWidth(input, activeDisplayValueText);
      if (textAlignment === 'left') setValueDisplayWidth(`${width}px`);
      else setValueDisplayWidth(undefined);
    }, [editing, activeDisplayValueText, textAlignment, isUnitClickPending]);

    const handleUnitClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!onUnitClick) return;
        const sequenceId = ++unitClickSequenceRef.current;

        try {
          const result = onUnitClick(event);
          if (!guards.is.promiseLike(result)) {
            setIsUnitClickPending(false);
            focusInputAtEnd();
            return;
          }

          setIsUnitClickPending(true);
          const settleIfLatest = () => {
            if (sequenceId !== unitClickSequenceRef.current) return;
            setIsUnitClickPending(false);
            focusInputAtEnd();
          };
          result.then(settleIfLatest, settleIfLatest);
        } catch (error) {
          setIsUnitClickPending(false);
          focusInputAtEnd();
          throw error;
        }
      },
      [focusInputAtEnd, onUnitClick],
    );

    const computedSlotProps = useMemo<
      NonNullable<TextFieldProps['slotProps']>
    >(() => {
      let unitElement: React.ReactNode = null;
      if (hasUnit) {
        if (onUnitClick) {
          unitElement = (
            <Box className={classes.unit}>
              <UnitButton onClick={handleUnitClick}>
                {displayData.unit}
              </UnitButton>
            </Box>
          );
        } else {
          unitElement = (
            <Typography
              component="span"
              variant="caption"
              className={classes.unit}
              onMouseDown={(e) => {
                // prevent clicking the unit causing the input being blurred
                e.preventDefault();
              }}
              onClick={() => {
                if (!htmlInputRef.current) return;
                if (isFocused) {
                  // already focused, so we just want to select the end of the input (as that's where the user clicked)
                  const endIndex = htmlInputRef.current.value.length;
                  htmlInputRef.current.setSelectionRange(endIndex, endIndex);
                } else htmlInputRef.current.focus();
              }}
              sx={{
                ml: 1,
              }}
            >
              {displayData.unit}
            </Typography>
          );
        }
      }

      return {
        input: {
          startAdornment,
          inputProps: {
            ref: combineRefs([htmlInputRef, inputRef]),
            className: classes.input,
            tabIndex: deactivated ? -1 : undefined,
            autoComplete: 'off',
            spellCheck: false,
            autoCorrect: 'off',
            autoCapitalize: 'off',
            readOnly: deactivated || undefined,
            'aria-label': ariaLabel,
          },
          endAdornment: unitElement,
        },
      };
    }, [
      ariaLabel,
      startAdornment,
      inputRef,
      classes.input,
      classes.unit,
      displayData.unit,
      deactivated,
      hasUnit,
      isFocused,
      handleUnitClick,
      onUnitClick,
    ]);

    const [isHovered, setIsHovered] = useState(false);
    const handleMouseEnter = useCallback(() => setIsHovered(true), []);
    const handleMouseLeave = useCallback(() => setIsHovered(false), []);

    const isActive = hasForcedActive
      ? true
      : isFocused || isHovered || isUnitClickPending;
    const tooltipMessage = unitNumberInputErrorTooltip({
      value,
      min,
      max,
      isFocused,
      showValidationError,
      submittedError: internalError?.message,
      errorMessage,
    });
    return (
      <Tooltip
        arrow
        open={
          onParseErrorChange
            ? false
            : isActive && guards.is.nonEmptyString(tooltipMessage)
        }
        variant="error"
        title={tooltipMessage}
      >
        <TextField
          ref={combineRefs([ref, rootRef])}
          id={id}
          name={name}
          className={cx(
            classes.textField,
            className,
            visualFocus && classes.activeClass,
          )}
          value={displayData.value}
          placeholder={placeholderText}
          onInput={handleInput}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleFocus}
          focused={visualFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDownInternal}
          error={Boolean(error || effectiveError || tooltipMessage)}
          helperText={helperText}
          slotProps={computedSlotProps}
          disabled={disabled}
          variant={variant}
          size={size}
          label={label}
          tabIndex={deactivated ? -1 : undefined}
        />
      </Tooltip>
    );
  },
);

const useStyles = makeStyles<{
  inputTextAlignment: 'left' | 'right';
  hasUnit: boolean;
  unitAlignment: 'tryVertical' | 'none';
  isFocused: boolean;
  deactivated: boolean;
  valueDisplayWidth: undefined | `${number}px`;
  outlineVisibility: 'hover' | 'always';
}>()((theme, params) => {
  const shouldTryToVerticalAlignUnit =
    params.hasUnit && params.unitAlignment === 'tryVertical';

  const unitMaxWidthStyles = {
    flexBasis: '37px',
    minWidth: '37px',
  };

  return {
    textField: {
      pointerEvents: params.deactivated ? 'none' : undefined,

      '& .MuiInputBase-root': {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
      },
      '& fieldset': {
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor:
          params.outlineVisibility === 'always'
            ? theme.palette.grey[400]
            : 'transparent',
      },
      '& .MuiOutlinedInput-root.Mui-disabled fieldset': {
        border: `1px solid ${params.outlineVisibility === 'always' ? theme.palette.grey[400] : 'transparent'}`,
      },
      '&:hover fieldset.MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.grey[400]}`,
      },
      '& .Mui-focused fieldset.MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.custom.focus}`,
      },
      '& .Mui-error fieldset.MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.error.main}`,
      },
      '&:hover .Mui-error fieldset.MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.error.main}`,
      },
    },
    input: {
      textAlign: params.isFocused ? 'right' : params.inputTextAlignment,
      alignSelf: 'baseline',
      flexBasis: params.valueDisplayWidth,
      width: params.valueDisplayWidth,
      fontSize: theme.typography.body2.fontSize,
      lineHeight: theme.typography.body2.lineHeight,
      fontWeight: theme.typography.body2.fontWeight,
      '&::placeholder': {
        color: theme.palette.text.secondary,
        opacity: 1,
      },
    },

    activeClass: {
      '& fieldset.MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.custom.focus}`,
      },
      '&:hover fieldset.MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.custom.focus}`,
      },
      '& .MuiInputBase-root': {
        backgroundColor: theme.palette.background.paper,
      },
    },
    unit: {
      alignSelf: 'baseline',
      cursor: 'inherit',
      ...(shouldTryToVerticalAlignUnit ? unitMaxWidthStyles : {}),
    },
  };
});
